import { type DeepPartial } from 'ts-essentials'

import { type RecursiveKeyOf } from '../types/helpers'

export const deepMergeOverwriteArray = (_destinationArray: Array<any>, sourceArray: Array<any>) => sourceArray

export function getObjectKeys<Obj extends object>(obj: Obj): Array<keyof Obj> {
  return Object.keys(obj) as Array<keyof Obj>
}

export function getObjectValues<Obj extends object>(obj: Obj): Array<Obj[keyof Obj]> {
  return Object.values(obj) as Array<Obj[keyof Obj]>
}
export function getObjectEntries<Obj extends object>(obj: Obj): Array<[keyof Obj, Obj[keyof Obj]]> {
  return Object.entries(obj) as Array<[keyof Obj, Obj[keyof Obj]]>
}

export function omit<O extends object, A extends keyof O>(object: O, attr: A): Omit<O, A> {
  if (attr in object) {
    const { [attr]: _, ...newObject } = object
    return newObject
  }

  return object
}

export function omitMany<O extends object, A extends keyof O>(object: O, attrs: A[]): Omit<O, A> {
  return attrs.reduce((acc, attr) => omit(acc, attr), object)
}

export function pick<O extends object, A extends keyof O>(object: O, attr: A): Pick<O, A> {
  return { [attr]: object[attr] } as Pick<O, A>
}

export function pickMany<O extends object, A extends keyof O>(object: O, attrs: A[]): Pick<O, A> {
  return attrs.reduce((acc, attr) => ({ ...acc, [attr]: object[attr] }), {} as Pick<O, A>)
}

export function excludePathsFromObject<T extends object>(obj: T, excludes: Array<RecursiveKeyOf<T>>): DeepPartial<T> {
  type ExcludePath = (typeof excludes)[0]

  const excludesSet = new Set(excludes)

  if (excludesSet.size === 0) return obj as DeepPartial<T>

  function filterObject(subObj: any, accObj: any, path?: string) {
    for (const key in subObj) {
      const fullPath = (path ? `${path}.${key}` : key) as ExcludePath

      if (excludesSet.has(fullPath)) continue

      if (typeof subObj[key] === 'object' && !Array.isArray(subObj[key])) {
        accObj[key] = {}
        filterObject(subObj[key], accObj[key], fullPath)
      } else {
        accObj[key] = subObj[key]
      }
    }

    return accObj
  }

  return filterObject(obj, {})
}

export function getObjectValueFromPath<T extends object>(obj: T, path: RecursiveKeyOf<T>, fallback?: any) {
  if (!path) return undefined
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)
  if (!pathArray) return fallback
  const result = pathArray.reduce((prevObj, key) => prevObj && prevObj[key as any], obj as any)
  return result === undefined ? fallback : result
}

export function randomArrayElement<T>(arr: T[]): T | undefined {
  return arr[Math.floor(Math.random() * arr.length)]
}

export function shuffleArray<T>(array: T[]): T[] {
  const newArray = array.slice()
  let currentIndex = newArray.length

  while (currentIndex != 0) {
    const randomIndex = Math.floor(Math.random() * currentIndex)
    currentIndex--
    ;[newArray[currentIndex], newArray[randomIndex]] = [newArray[randomIndex], newArray[currentIndex]]
  }

  return newArray
}

export function isStringInTuple<T extends readonly string[]>(tuple: T, str: string): str is T[number] {
  return tuple.includes(str)
}

export function localeCompareSort<T extends object>(arr: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] {
  return arr.slice().sort((a, b) => {
    const aKey = a[key] as any
    const bKey = b[key] as any

    if (aKey === bKey) return 0

    return direction === 'asc'
      ? aKey.localeCompare(bKey, undefined, { sensitivity: 'base', numeric: true })
      : bKey.localeCompare(aKey, undefined, { sensitivity: 'base', numeric: true })
  })
}

export function dedupeArray<T extends object>(arr: T[], key: keyof T): T[] {
  return arr.filter((v, i, a) => a.findIndex(t => t[key] === v[key]) === i)
}

export function removeNullishValuesFromObject<T extends object>(obj: T): Partial<T> {
  const newObj = {} as Partial<T>

  for (const key in obj) {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      newObj[key] = obj[key]
    }
  }

  return newObj
}
